import { inject, Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { DialogButton, SendMessageDialogResult } from 'src/app/interfaces/dialog.interface';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { UserBasic } from 'src/app/swagger-api/model/userBasic';

@Injectable({ providedIn: 'root' })
export class ChatDialogsService {
  private notificationService = inject(NotificationService);
  private notificationContextService = inject(NotificationContextAttributesService);

  public showNotFoundConversationDialog(): void {
    const subscription: Subscription = this.notificationService
      .dialogDefault({
        title: 'CHAT_CONVERSATION_NOT_FOUND_TITLE_HINT',
        description: 'CHAT_CONVERSATION_NOT_FOUND_DESCRIPTION_HINT',
        buttons: this.notificationContextService.getDefaultCloseAttr(),
      })
      .subscribe(() => subscription.unsubscribe());
  }

  public showSendMessageDialogWithSelectedUsers(
    users: UserBasic[],
    callback: (data: SendMessageDialogResult) => void,
  ): void {
    const subscription: Subscription = this.notificationService
      .dialogSendMessage({
        users,
        buttons: [
          this.notificationContextService.createDialogButton(
            'CHAT_MESSAGE_FORM_SEND_BUTTON_HINT',
            'btn-primary',
            DialogActionTypes.SUBMIT_MESSAGE,
          ),
        ],
      })
      .subscribe((data: SendMessageDialogResult | boolean) => {
        subscription.unsubscribe();
        if (this.hasParticipants(data)) {
          callback(data as SendMessageDialogResult);
        }
      });
  }

  public showSendMessageDialogWithoutSelectedUsers(callback: (data: SendMessageDialogResult) => void): void {
    const subscription: Subscription = this.notificationService
      .dialogSendMessage({
        users: [],
        buttons: [
          this.notificationContextService.createDialogButton(
            'BTN_SEND_HINT',
            'btn-primary',
            DialogActionTypes.INIT_CHAT_ROOM,
          ),
        ],
      })
      .subscribe((data: SendMessageDialogResult | boolean) => {
        subscription.unsubscribe();
        if (this.hasParticipants(data)) {
          callback(data as SendMessageDialogResult);
        }
      });
  }

  public showFailedToSendMessage(): void {
    const subscription: Subscription = this.notificationService
      .dialogDefault({
        title: 'CHAT_SENDING_MESSAGE_FAILED_TITLE_HINT',
        description: 'CHAT_SENDING_MESSAGE_FAILED_DESCRIPTION_HINT',
        buttons: this.notificationContextService.getDefaultCloseAttr(),
      })
      .subscribe(() => subscription.unsubscribe());
  }

  public showSentSuccessfulMessage(): void {
    this.notificationService.showSuccessfulMessage('CHAT_SENT_SUCCESSFUL_MESSAGE');
  }

  public blockUserDialog(callback: () => void): void {
    const subscription: Subscription = this.notificationService
      .dialogDefault({
        title: 'CHAT_BLOCK_USER_DIALOG_TITLE_HINT',
        description: 'CHAT_BLOCK_USER_DIALOG_DESCRIPTION_HINT',
        buttons: this.getDialogButtons('CHAT_BLOCK_USER_DIALOG_BLOCK_BUTTON_HINT'),
      })
      .subscribe((response: string | boolean) => {
        subscription.unsubscribe();
        if (response) {
          callback();
        }
      });
  }

  public unBlockUserDialog(callback: () => void): void {
    const subscription: Subscription = this.notificationService
      .dialogDefault({
        title: 'CHAT_UNBLOCK_USER_DIALOG_TITLE_HINT',
        description: 'CHAT_UNBLOCK_USER_DIALOG_DESCRIPTION_HINT',
        buttons: this.getDialogButtons('CHAT_BLOCK_USER_DIALOG_UNBLOCK_BUTTON_HINT'),
      })
      .subscribe((response: string | boolean) => {
        subscription.unsubscribe();
        if (response) {
          callback();
        }
      });
  }

  public showChangeConversationTitleEditDialogue(conversationTitle: string): Observable<string> {
    return this.notificationService.dialogConversationTitleEdit({
      title: 'CHAT_EDIT_CONVERSATION_TITLE',
      description: conversationTitle,
      buttons: [
        this.notificationContextService.createDialogButton(
          'CHAT_EDIT_CONVERSATION_TITLE_RESET_BUTTON',
          'btn-outline-primary',
          DialogActionTypes.RESET_CONVERSATION_TITLE,
        ),
        this.notificationContextService.createDialogButton('BTN_SUBMIT_HINT', 'btn-primary', DialogActionTypes.SUBMIT),
      ],
    });
  }

  private getDialogButtons(buttonTitle = 'Clear'): DialogButton[] {
    return [
      this.notificationContextService.createDialogButton(
        'BTN_CANCEL_HINT',
        'btn-outline-secondary',
        DialogActionTypes.CLOSE_DIALOG,
      ),
      this.notificationContextService.createDialogButton(buttonTitle, 'btn-primary', DialogActionTypes.CONFIRM_ACTION),
    ];
  }

  private hasParticipants(data: SendMessageDialogResult | boolean): boolean {
    return typeof data !== 'boolean' && data && data.participants && data.participants.length > 0;
  }
}
